import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cn } from '../../common/functions/cn';
import useTestTag from '../../common/hooks/use-test-tag';
import OdinIcon, { type OdinIconName, OdinIconSize } from '../odin-icon/odin-icon.component';

export type ButtonType = 'default' | 'primary' | 'text';
export type ButtonVariant = 'regular' | 'alternative';
export type ButtonHtmlType = 'submit' | 'reset' | 'button';

export interface ButtonProps extends Omit<ButtonPrimitive.Props, 'type' | 'style'> {
  type?: ButtonType;
  variant?: ButtonVariant;
  classNames?: string;
  fullWidth?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  htmlType?: ButtonHtmlType;
  style?: React.CSSProperties;
}

interface IconButtonProps extends Omit<ButtonProps, 'type' | 'children' | 'classNames'> {
  type?: ButtonHtmlType;
  icon: OdinIconName;
  iconSize?: OdinIconSize;
  iconClassName?: string;
  spin?: boolean;
  showFocus?: boolean;
  /** Optional tooltip text; applied to title & aria-label for accessibility */
  tooltip?: string;
}

const commonButtonStyles =
  'inline-flex sm:w-auto text-md leading-5 rounded-sm border outline-none justify-center transition-colors duration-200 min-w-[3rem] min-h-[2.25rem] py-2 px-5 font-medium focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50';

const buttonStylesMap: Record<ButtonType, Record<ButtonVariant, string>> = {
  primary: {
    regular: 'bg-primary hover:bg-primary/90 text-primary-foreground border-transparent',
    alternative: 'bg-primary hover:bg-primary/90 text-primary-foreground border-transparent',
  },
  default: {
    regular: 'bg-background hover:bg-accent text-primary border border-solid border-primary',
    alternative: 'hover:bg-accent text-primary border border-primary',
  },
  text: {
    regular: 'text-primary hover:text-primary/80 bg-transparent border-transparent',
    alternative: 'text-primary hover:bg-primary/40 hover:text-foreground bg-transparent border-transparent',
  },
};

const getButtonStyles = (type: ButtonType, variant: ButtonVariant) => {
  const styles = buttonStylesMap[type] || buttonStylesMap.default;
  return styles[variant];
};

export default function Button({
  type = 'default',
  variant = 'regular',
  classNames,
  className,
  fullWidth = true,
  hidden = false,
  disabled = false,
  htmlType = 'button',
  form,
  ...props
}: ButtonProps) {
  const buttonStyles = cn(
    commonButtonStyles,
    { 'w-full': fullWidth },
    { 'opacity-50': disabled },
    getButtonStyles(type, variant),
    classNames,
    className,
    hidden && 'hidden'
  );

  return (
    <ButtonPrimitive
      data-slot="button"
      type={htmlType}
      className={buttonStyles}
      form={form}
      disabled={disabled}
      {...props}
    />
  );
}

export function IconButton({
  onClick,
  className,
  type = 'button',
  form,
  disabled,
  icon,
  iconSize = OdinIconSize.Small,
  iconClassName,
  spin = false,
  showFocus = true,
  tooltip,
  ...rest
}: IconButtonProps) {
  const tagCreator = useTestTag('icon-button');
  const buttonStyles = cn(
    'focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2',
    { 'cursor-not-allowed opacity-50': disabled },
    className
  );

  return (
    <button
      type={type}
      onClick={onClick}
      className={buttonStyles}
      form={form}
      disabled={disabled}
      ref={tagCreator(icon.trim() ? icon.replace(/\s+/g, '-').toLowerCase() : '')}
      title={tooltip}
      aria-label={tooltip}
      {...rest}
    >
      <OdinIcon icon={icon} size={iconSize} className={iconClassName} spin={spin} showFocus={showFocus} />
    </button>
  );
}

export function Link({ classNames, className, type: _type, style, ...props }: ButtonProps) {
  const linkStyles = cn(
    'm-1 bg-transparent p-0 text-md text-primary leading-5 underline hover:no-underline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-4',
    classNames,
    className
  );

  return <button className={linkStyles} type="button" style={style} {...props} />;
}
